<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const campaignToDelete = ref(null);
const shouldHardDelete = ref(false);

const dialogTitle = computed(() =>
  shouldHardDelete.value
    ? 'Excluir campanha definitivamente?'
    : t('CAMPAIGN.CONFIRM_DELETE.TITLE')
);

const dialogDescription = computed(() =>
  shouldHardDelete.value
    ? 'Esta ação remove a campanha do banco de dados e não pode ser desfeita.'
    : 'A campanha ficará inativa e não será removida do banco de dados.'
);

const confirmLabel = computed(() =>
  shouldHardDelete.value ? 'Excluir definitivamente' : 'Inativar campanha'
);

const open = (campaign, hardDelete = false) => {
  campaignToDelete.value = campaign;
  shouldHardDelete.value = hardDelete;
  dialogRef.value?.open();
};

const deleteCampaign = async id => {
  if (!id) return;

  try {
    await store.dispatch(
      shouldHardDelete.value ? 'campaigns/hardDelete' : 'campaigns/delete',
      id
    );
    await store.dispatch('campaigns/get');
    useAlert(
      shouldHardDelete.value
        ? 'Campanha excluída definitivamente.'
        : 'Campanha inativada.'
    );
  } catch (error) {
    useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.ERROR_MESSAGE'));
    throw error;
  }
};

const handleDialogConfirm = async () => {
  try {
    await deleteCampaign(
      campaignToDelete.value?.id || props.selectedCampaign?.id
    );
    dialogRef.value?.close();
    campaignToDelete.value = null;
    shouldHardDelete.value = false;
  } catch {
    // Keep the dialog open so the user can retry or cancel after an API error.
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="dialogTitle"
    :description="dialogDescription"
    :confirm-button-label="confirmLabel"
    @confirm="handleDialogConfirm"
  />
</template>
